import { Card } from '@avanta/shared/components/ui/Card';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type PatientRecord } from 'types/patient';
import {
  formatRecordDate,
  getRecordContractCode,
  getSickLeaveStartDate,
} from '../../utils/records';

const EMPTY_VALUE = '—';

interface ActivePatientRecordProps {
  record: PatientRecord | null | undefined;
  isLoading: boolean;
}

export const ActivePatientRecord = ({
  record,
  isLoading,
}: ActivePatientRecordProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  return (
    <Card
      variant="primary"
      title={t('PatientRecords.active.heading')}
      icon={FolderOpen}
      className="w-full p-0 am-md:p-0"
    >
      {isLoading ? (
        <p className="text-denim-800 px-8 py-6 text-sm">
          {t('Common.loading', { ns: LOCIZE_NS.COMMON })}
        </p>
      ) : record ? (
        <div className="grid grid-cols-1 gap-6 px-8 py-6 am-lg:grid-cols-3">
          <Input
            type="text"
            className="w-full"
            label={t('PatientRecords.fields.code')}
            value={record.code || EMPTY_VALUE}
            readOnly
            variant="filled"
          />
          <Input
            type="text"
            className="w-full"
            label={t('PatientRecords.fields.contract')}
            value={getRecordContractCode(record)}
            readOnly
            variant="filled"
          />
          <Input
            type="text"
            className="w-full"
            label={t('PatientRecords.fields.sickLeaveStart')}
            value={formatRecordDate(getSickLeaveStartDate(record))}
            readOnly
            variant="filled"
          />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 px-8 py-12 text-center">
          <p className="text-denim-800 text-base leading-5 font-medium">
            {t('PatientRecords.active.empty.title')}
          </p>
          <p className="text-gray-500 max-w-lg text-sm leading-4.5">
            {t('PatientRecords.active.empty.description')}
          </p>
        </div>
      )}
    </Card>
  );
};
